// src/services/authService.js
import { fetchFromBackend } from "./apiClient";
import { TOKEN_KEY, USER_KEY } from "../config";
import {
  validateRequired,
  validateEmail,
  validateStrongPassword,
  validatePasswordConfirmation,
  validateUsername,
} from "../utils/validationUtils"; // Adjust path as needed

const API_ACCOUNT_ENDPOINT = "api/Account";

const handleApiError = (error, functionName) => {
  // If the error message is already user-friendly (e.g., from our client-side validation), use it directly.
  // Otherwise, try to parse backend-specific error structures.
  let message = error.details ? error.details.detail : error.message; // Default to the error's message (good for our custom validation errors)

  // Check for common backend error structures
  if (!message || message.includes("fetchFromBackend")) {
    // If message is generic or non-existent
    if (error.details?.detail) {
      message = error.details.detail;
    } else if (error.response?.data?.message) {
      // Common with Axios
      message = error.response.data.message;
      if (error.response.data.errors) {
        // Append detailed field errors if available
        const fieldErrors = Object.values(error.response.data.errors)
          .flat()
          .join(" ");
        if (fieldErrors) message += ` ${fieldErrors}`;
      }
    } else {
      message = `An unexpected error occurred in ${functionName}. Please try again.`;
    }
  }

  console.error(`Error in ${functionName}:`, error); // Log the original error for debugging
  throw new Error(message); // Throw a new error with a user-friendly message
};

export const loginUser = async (email, password) => {
  try {
    // Client-side validation
    validateEmail(email);
    validateRequired(password, "Password");
    // Minimum length check for login can be simpler than full strong password check,
    // as the main strong password validation happens at registration/reset.
    // However, if your API enforces it strictly on login too, you might add it.
    if (password.length < 6) {
      // Basic length check for login
      throw new Error("Password must be at least 6 characters long.");
    }

    const response = await fetchFromBackend(
      `${API_ACCOUNT_ENDPOINT}/authenticate`,
      {
        body: { email, password },
      },
      "POST"
    );

    if (response && response.data && response.data.jwToken) {
      // localStorage.setItem(TOKEN_KEY, response.data.jwToken);
      // localStorage.setItem(USER_KEY, JSON.stringify(response.data));
      return response.data;
    }
    // If token is missing but no error was thrown by fetchFromBackend
    throw new Error(
      response.message || "Login failed: Invalid server response."
    );
  } catch (error) {
    handleApiError(error, "loginUser");
  }
};
export const resendVerification = async (email) => {
  try {
    // Client-side validation
    validateEmail(email);

    const response = await fetchFromBackend(
      `${API_ACCOUNT_ENDPOINT}/resendEmailConfirmation?userEmail=${encodeURIComponent(
        email
      )}`,
      {}, // No body for GET
      "POST"
    );

    if (response && response.data && response.data.jwToken) {
      // localStorage.setItem(TOKEN_KEY, response.data.jwToken);
      // localStorage.setItem(USER_KEY, JSON.stringify(response.data));
      return response.data;
    }
    // If token is missing but no error was thrown by fetchFromBackend
    throw new Error(
      response.message || "Resend verification failed: Invalid server response."
    );
  } catch (error) {
    handleApiError(error, "resendVerification");
  }
};

export const registerUser = async (userData) => {
  // userData: { userName, firstName, lastName, email, password, confirmPassword }
  try {
    const { userName, firstName, lastName, email, password, confirmPassword } =
      userData;

    // Client-side validation
    validateUsername(userName, "Username");
    validateRequired(firstName, "First Name");
    validateRequired(lastName, "Last Name");
    validateEmail(email);
    validateStrongPassword(password); // Uses default "Password" as fieldName
    validatePasswordConfirmation(password, confirmPassword);

    const response = await fetchFromBackend(
      `${API_ACCOUNT_ENDPOINT}/register`,
      {
        body: userData, // Send the whole userData object
      },
      "POST"
    );
    return response;
  } catch (error) {
    handleApiError(error, "registerUser");
  }
};

export const forgotPassword = async (email) => {
  try {
    // Client-side validation
    validateEmail(email);

    const response = await fetchFromBackend(
      `${API_ACCOUNT_ENDPOINT}/forgotPassword`,
      {
        body: { email },
      },
      "POST"
    );
    return response;
  } catch (error) {
    handleApiError(error, "forgotPassword");
  }
};

export const resetPassword = async (
  email,
  code,
  newPassword,
  confirmNewPassword
) => {
  try {
    // Client-side validation
    validateEmail(email);
    validateRequired(code, "Reset Code");
    validateStrongPassword(newPassword, "New Password");
    validatePasswordConfirmation(newPassword, confirmNewPassword); // Important to confirm the new password

    const response = await fetchFromBackend(
      `${API_ACCOUNT_ENDPOINT}/resetPassword`,
      {
        // API expects: email, token, password, confirmPassword
        // 'token' is our 'code', 'password' is 'newPassword'
        body: {
          email,
          token: code,
          password: newPassword,
          confirmPassword: newPassword,
        },
      },
      "POST"
    );
    return response;
  } catch (error) {
    handleApiError(error, "resetPassword");
  }
};

export const confirmEmail = async (userId, code) => {
  try {
    const response = await fetchFromBackend(
      `${API_ACCOUNT_ENDPOINT}/confirmEmail?userId=${encodeURIComponent(
        userId
      )}&code=${encodeURIComponent(code)}`,
      {}, // No body for GET
      "GET" // Method is GET as per curl
    );
    // API returns: { message: "string", data: "string" }
    return response;
  } catch (error) {
    const message = error.details.detail || "An error occurred during login";
    console.error("Error in loginUser:", error);
    throw new Error(message);
  }
};

export const logoutUser = () => {
  try {
    const { user } = getStoredAuth();
    if (!user) {
      console.warn("No user is currently logged in. Skipping logout.");
      return;
    }
    const email = user.email || user.userName || user.id;
    if (!email) {
      console.warn("No user email found. Skipping backend logout.");
      return;
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch (error) {
    console.error("Error during logout:", error);
  } finally {
    // Clear local storage
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  // Potentially call a backend /logout endpoint if it exists to invalidate server-side session/token
};
export const refreshToken = async () => {
  try {
    const { user } = getStoredAuth();
    if (!user) {
      console.warn("No user is currently logged in. Skipping token refresh.");
      return;
    }
    const email = user.email || user.userName || user.id;
    if (!email) {
      console.warn("No user email found. Skipping token refresh.");
      return;
    }
    const token = user.refreshToken;
    const response = await fetchFromBackend(
      `${API_ACCOUNT_ENDPOINT}/refreshToken`,
      {
        body: { email, token },
      },
      "POST"
    );
    localStorage.setItem(TOKEN_KEY, response.data.jwToken);
    localStorage.setItem(USER_KEY, JSON.stringify(response.data));
    return response.data;
  } catch (error) {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    handleApiError(error, "refreshToken");
  }
};
export const isAuthenticated = async () => {
  try {
    const { token, user } = getStoredAuth();
    // API returns: { message: "string", data: "string" }
    return token && user;
  } catch (error) {
    throw new Error(error);
  }
};
export const getStoredAuth = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  const userString = localStorage.getItem(USER_KEY);
  const user = userString ? JSON.parse(userString) : null;
  return { token, user };
};
